import React, { useState, useEffect } from 'react';
import { Check, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { packagesAPI } from '../utils/api';
import { defaultAdPackages } from '../mock';

export const Advertisers = () => {
  const { toast } = useToast();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [adPackages, setAdPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await packagesAPI.getAll();
        if (response.data && response.data.length > 0) {
          setAdPackages(response.data);
        } else {
          // Fallback to mock data if no packages in DB
          setAdPackages(defaultAdPackages);
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Fallback to mock data on error
        setAdPackages(defaultAdPackages);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const handlePurchase = (pkg) => {
    setSelectedPackage(pkg);
    // Mock payment flow - Stripe integration deferred
    toast({
      title: "Payment Processing",
      description: `Initiating payment for ${pkg.name} package. Stripe integration coming soon!`,
    });

    // Simulate payment processing
    setTimeout(() => {
      toast({
        title: "Request Received!",
        description: `Thank you for your interest in the ${pkg.name} package. Our team will contact you shortly.`,
      });
      setSelectedPackage(null);
    }, 2000);
  };

  return (
    <section id="advertisers" className="py-20 bg-white" data-testid="advertisers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full mb-4">
            <TrendingUp className="w-5 h-5 text-emerald-700" />
            <span className="text-sm font-semibold text-emerald-700">Partner with Us</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Advertising & Partnerships
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Reach thousands of potential residents, homebuyers, and business owners exploring Cornwall as their next destination
          </p>
        </div>

        {/* Target Audience Info */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 mb-16 border border-blue-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">Who You'll Reach</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-3xl font-bold text-emerald-600 mb-2">15K+</div>
              <div className="text-sm font-medium text-slate-700">Monthly Visitors</div>
              <div className="text-xs text-slate-500 mt-1">Growing 25% quarterly</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-3xl font-bold text-blue-600 mb-2">62%</div>
              <div className="text-sm font-medium text-slate-700">Relocation Intent</div>
              <div className="text-xs text-slate-500 mt-1">Actively planning moves</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-3xl font-bold text-teal-600 mb-2">40%</div>
              <div className="text-sm font-medium text-slate-700">Business Decision Makers</div>
              <div className="text-xs text-slate-500 mt-1">Evaluating Cornwall for expansion</div>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-slate-500">Loading packages...</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {adPackages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  pkg.popular
                    ? 'border-4 border-emerald-500 shadow-2xl scale-105'
                    : 'border-2 hover:border-emerald-300 hover:shadow-xl'
                }`}
                data-testid={`package-card-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-1 rounded-bl-lg flex items-center space-x-1">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-bold">Most Popular</span>
                  </div>
                )}

                <CardHeader className="space-y-4 pt-8">
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    {pkg.name}
                  </CardTitle>
                  <div className="space-y-2">
                    <div className="flex items-baseline space-x-2">
                      <span className="text-5xl font-bold text-emerald-600">${pkg.price}</span>
                      <span className="text-slate-500">/ {pkg.duration}</span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {pkg.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => handlePurchase(pkg)}
                    disabled={selectedPackage?.id === pkg.id}
                    className={`w-full ${
                      pkg.popular
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-slate-900 hover:bg-slate-800'
                    } text-white shadow-md hover:shadow-lg transition-all py-6 text-lg`}
                    data-testid={`purchase-btn-${pkg.id}`}
                  >
                    {selectedPackage?.id === pkg.id ? 'Processing...' : 'Get Started'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* AdSense Banner Placements Info */}
        <div className="bg-slate-50 rounded-2xl p-8 mb-16 border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Banner Ad Placements</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900">Available Banner Sizes</h4>
              <div className="space-y-2">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="font-medium text-slate-900">Leaderboard</div>
                  <div className="text-sm text-slate-600">728 x 90 pixels - Header placement</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="font-medium text-slate-900">Medium Rectangle</div>
                  <div className="text-sm text-slate-600">300 x 250 pixels - Sidebar & content</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="font-medium text-slate-900">Wide Skyscraper</div>
                  <div className="text-sm text-slate-600">160 x 600 pixels - Sidebar placement</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-slate-900">Google AdSense Compatible</h4>
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <p className="text-slate-700 mb-4">
                  All banner placements are fully compatible with Google AdSense standards and policies.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>Standard IAB ad unit sizes</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>Responsive design support</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>Multiple placement options</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>Performance tracking included</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Partnership CTA */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200 text-center">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            Need a Custom Partnership?
          </h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            Looking for sponsored quiz features, exclusive content partnerships, or custom campaign packages? 
            Reach out to us at <a href="mailto:info@movetocornwall.ca" className="font-semibold text-emerald-600 hover:text-emerald-700">info@movetocornwall.ca</a>
          </p>
        </div>
      </div>
    </section>
  );
};
